import { useEffect, useState } from 'react';
import { Button, FormGroup, Tabs, Tab, FormLabel, Card } from 'react-bootstrap';
import { FiletypePdf, Share } from 'react-bootstrap-icons';
import { useLocation, useNavigate } from 'react-router';
import { getDB } from '../util/db';
import { SurveyForm } from '../forms/SurveyForm';
import { Inspection } from "../models/Inspection";
import { MachineForm } from '../forms/MachineForm';
import { useParams } from 'react-router';
import { useProfile } from '../components/ProfileContext';
import { afterRenderDelay, downloadPdfDocumentToNewWindow, emptyArray, formatFilename, isoTimestamp, listMemberForId, pageReadyCondition, setNavAdvisoryLockout, stringToDate, useChainedState, useForceRender } from '../util/utils';
import { FinalizationForm } from '../forms/FinalizationForm';
import { LoadingComponent } from '../components/LoadingComponent';
import { InspectionJobForm } from '../forms/InspectionJobForm';
import { Survey } from '../models/Survey';
import { ArrowRight } from 'react-bootstrap-icons';
import { PartsForm } from '../forms/PartsForm';
import { SpinnerButton } from '../components/SpinnerButton';
import { SharingFormModal } from '../forms/SharingFormModal';
import { GroupedObjectSelector } from '../components/GroupedObjectSelector';
import { SimpleFormFieldState } from '../components/SimpleFormFieldState';
import { SimpleFormField } from '../components/SimpleFormField';



function with_warning_label(text) {

	return (
	<span>{text} {<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="red" className="bi bi-exclamation-circle-fill" viewBox="0 0 16 16">
		<path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
		</svg>}
		</span>
	);
}



export function InspectionPage(props) {
	setNavAdvisoryLockout(true);

	// *** State declarations *** //
	let location = useLocation();
	let initialState = location.state;

	// Navigate Parameters
	// When passed through "navigate" this gets dehydrated for some reason
	let initialInspection = Inspection.hydrate(initialState?.inspection);
	let defaultAssetNumber = initialState?.assetNumber;
	if( initialInspection?.components){
		initialInspection.inspectionComponents = initialState?.inspection?.components;
	}
	initialInspection?.populateCTInComponents(initialInspection.primaryComponentTypeId);

	// Global State
    let [profile, setProfile] = useProfile();

	// Global Database state
    let [surveys, setSurveys] = useState(null);
	let [jobs, setJobs] = useState(null);
	let [pdfBuilding, setPdfBuilding] = useState(false);

	// Page load state
	let [inspection, setInspection] = useState(initialInspection || new Inspection({inspector: profile, job: profile?.job}));
	let isReadOnly = inspection.syncStatus == "READONLY";
	let [readyFinal, setReadyFinal] = useState( (!inspection.rentalMode || !!inspection.rentalDisposition) && !!inspection.result && !!inspection.disposition );
	const forceRerender = useForceRender();

	if(isReadOnly) {
		setNavAdvisoryLockout(false);
	}
	
	// In-page state
	let [surveyId, setSurveyId] = useChainedState(inspection.surveyId, (newSurveyId) => {
		if(inspection.surveyId != newSurveyId) {
			// When the user updates the survey ID, update it on the model
			inspection.surveyId = newSurveyId;

			// When the survey is selected, if there is a default asset number, be sure it is set
			if(!!defaultAssetNumber) {
				let tmpSurvey = listMemberForId(surveys, newSurveyId);
				let ctid = tmpSurvey.surveyComponentTypes[0].componentTypeId;
				let ic = inspection.getInspectionComponentFor(ctid);
				if(!(ic.assetNumber)) {
					ic.assetNumber = defaultAssetNumber;
				}
			}
		}
	});
	let [tabKey, setTabKey] = useState(initialState?.startTab || "parts");

	// Other
    let params = useParams();
	let navigate = useNavigate();

	// Derivative states
    let originalInspectionId = params.inspectionId;
	let needsLoadingInspection = originalInspectionId != null && originalInspectionId != inspection.id;
	let pageCondition = pageReadyCondition(surveys != null, jobs != null, !needsLoadingInspection);
	let [isLoadingInspection, setIsLoadingInspection] = useState(false);
	let survey = listMemberForId(surveys, surveyId);
	let [jobId, setJobId] = useState(inspection.jobId);
	let job = listMemberForId(jobs, jobId);
	let connections = profile?.getConnections() || emptyArray;
	let [showSharingDialog, setShowSharingDialog] = useState(false);
	const [surveyIncomplete, setsurveyIncomplete] = useState(false);
	const [assetNumber, setAssetNumber] = useState(inspection.componentAssetNumber);
	
	
	useEffect(async () => {
		let db = await getDB();
		if(jobs == null) {
			setJobs(await db.getAll("Jobs"));
		}
		if(surveys == null) {
			setSurveys(await Survey.getActiveSurveys());
		}
		let hasMissingAnswer = (survey != null || job != null) && inspection.hasUnansweredQuestion(survey, job);
		setsurveyIncomplete(hasMissingAnswer);
	});
	useEffect(() => {
		if(jobs != null  && surveys != null && needsLoadingInspection && (!isLoadingInspection)) {
			setIsLoadingInspection(true);
			Inspection.find(originalInspectionId).then(insp => {
				if(insp != null) {
					if(insp.syncStatus != "READONLY") {
						// If someone hits "save for later", make sure we know it needs to be sync'd
						insp.syncStatus = "EDIT";
					}
					console.log("Setting inspection");
					setInspection(insp);
					setAssetNumber(insp.componentAssetNumber);
					setJobId(insp.jobId);

	
					// If this was for a deleted survey, load it first
					let tmpSurvey = listMemberForId(surveys, insp.surveyId);
					if(tmpSurvey == null) {
						Survey.getActiveSurveys(insp.surveyId).then(surveys => {
							setSurveys(surveys);
							setSurveyId(insp.surveyId);
						})
					} else {
						setSurveyId(insp.surveyId);
					}
				}
			})
		}
	});


	useEffect(() => {
		inspection.setAssetNumber(assetNumber);
		setInspection(inspection);
	}, [assetNumber])


	useEffect(() => {
		setReadyFinal((!inspection.rentalMode || !!inspection.rentalDisposition) && !!inspection.result && !!inspection.disposition );
	}, [inspection]);

	let jobChangedAction = () => {
		setJobId(inspection.jobId);
	};

    useEffect(() => {
        if(profile == null && !params.sharingCode) {
            navigate("/login");
        }    
    })

    let submitForm = async (evt) => {
        if(navigator.onLine) {
			try {
				inspection.completedAt = isoTimestamp();
				let newInspection = await inspection.sendToServer(profile);
				if(newInspection == null) {
					throw "Unknown error saving inspection";
				}
                alert("Successfully saved inspection to the Vault™.");

				// NOTE - since newInspection is a different object, must either navigate
				// or run setInspection here.
				navigate("/dashboard");
			} catch(err) {
				console.log(err);
				inspection.completedAt = null; // Reset if it didn't complete
                alert(`Could not save inspection to server: ${err}`);
			}
        } else {
            alert("You are currently offline and the inspection could not be saved to the Vault™.")
        }
    }

    let saveForm = async (evt) => {
        let db = await getDB();
        db.put("Inspections", inspection);
        navigate("/dashboard");
    };

	let saveWhileEditing = async() => {
		let db = await getDB();
		db.put("Inspections", inspection);
	}

	// No longer used because we are already auto-saving
    let discardForm = (evt) => {
        if(confirm("Are you sure you want to discard your changes?")) {
            navigate("/dashboard");
        }
    };

	let generatePdfAction = () => {
		setPdfBuilding(true);
		afterRenderDelay(async () => {
			let pdf = await Inspection.generatePdfDocumentFromInspections(profile, [inspection]);
			let tmpJob = listMemberForId(jobs, inspection.jobId);
			await downloadPdfDocumentToNewWindow(pdf, formatFilename("pdf", profile?.dateFormatter(stringToDate(inspection.completedAt)) || "inspection", tmpJob?.name, inspection.componentAssetNumber));
			setPdfBuilding(false);	
		});
	};

	let goToTab = (tabname) => () => {
		setTabKey(tabname);
		setTimeout(() => {
			document.getElementById("screen-top").scrollIntoView();
		}, 100)
	};

	let shareAction = () => {
		setShowSharingDialog(!showSharingDialog);
	};

	let tabContentStyle = "p-4 bg-light mb-4"
	
	const mainPage =  function() {
		return (
			<div className='inspectionPageContainer'>
				<h1>
					Inspection
				</h1>
				<div id="screen-top"></div>{/* Marker for scrolling */}

				<hr />
				<div className='topButtons'>
					{!isReadOnly && <>

						{/* <Button className="float-end ms-2" size="sm" variant="danger" onClick={discardForm}>Discard Changes</Button>
						<Button className="float-end ms-2" size="sm" variant="secondary" onClick={saveForm}>Save for Later</Button>
						<Button className="float-end mt-2 ms-2" size="sm" onClick={generatePdfAction}>PDF</Button> */}
						<div className='discardButtons mt-2'>
							<Button className="save"  onClick={saveForm}>Save for Later</Button>
						</div>
					</>}

					{inspection.completedAt != null && <div>
						{inspection.userIdentifier == profile?.id?.toString() && <Button onClick={shareAction}><Share /></Button>}
						<SpinnerButton className="filetypeButton" spinning={pdfBuilding} spinnerStyle={{color: "black"}} onClick={generatePdfAction}><FiletypePdf className='filetypeIcon'/></SpinnerButton>
					</div>}
				</div>
				<SharingFormModal show={showSharingDialog} setShow={setShowSharingDialog} sharingObject={inspection} profile={profile} sharingLink={inspection.sharingLink} />
				<div className="clearfix"></div>
				<hr />
				<Tabs className='tabContainer' activeKey={tabKey} onSelect={(k) => setTabKey(k)}>
					<Tab eventKey="setup" title="Setup">
						<div className={tabContentStyle}>
							<fieldset disabled={isReadOnly}>
								<Card>
									<Card.Body>
										<FormGroup>
											<FormLabel style={{color:"#007041", marginRight:"0.8em"}}>Inspector:</FormLabel>
											{!!inspection.inspectorIdentifier ? inspection.inspectorIdentifier : "None"}
										</FormGroup>
										<FormGroup>
											<FormLabel style={{color:"#007041"}}>Equipment Type</FormLabel>
											<GroupedObjectSelector groupedOptions={Survey.groupSurveysByCategory(surveys)} defaultSelected={surveyId} labelAttribute="displayName" onChange={(e) => setSurveyId(e.target.value)}></GroupedObjectSelector>
										</FormGroup>
										<FormGroup>
											<FormLabel style={{color:"#007041"}}> Asset Number (Required) </FormLabel>
											<SimpleFormFieldState scannable={false} id="inspection_asset" name="inspection_asset" value={assetNumber} setValue={setAssetNumber}></SimpleFormFieldState>
										</FormGroup>
										<FormGroup>
											<FormLabel style={{color:"#007041"}}> Asset Description </FormLabel>
											<SimpleFormField scannable={false} bindObject={inspection} bindAttribute="assetDescription" />
										</FormGroup>									</Card.Body>
								</Card>


								<InspectionJobForm jobs={jobs} inspection={inspection} onJobChange={jobChangedAction} saveAction={saveWhileEditing} />

								{survey != null && <MachineForm inspection={inspection} survey={survey} job={job} saveAction={saveWhileEditing} />}

							</fieldset>
							<button className="mt-4 buttonFullWidth" onClick={goToTab("survey")}>Continue <ArrowRight /></button>
						</div>
					</Tab>
					<Tab eventKey="survey" title={surveyIncomplete ? with_warning_label("Checks"): "Checks" }  disabled={survey == null}>
						<div className={tabContentStyle}>
							<fieldset disabled={isReadOnly}>
								{ survey != null && <SurveyForm survey={survey} inspection={inspection} job={job} saveAction={saveWhileEditing} onChange={() => {
									setsurveyIncomplete((survey != null || job != null) && inspection.hasUnansweredQuestion(survey, job));
									forceRerender();
								}} />}
							</fieldset>         
							<button className="mt-4 buttonFullWidth" onClick={goToTab("parts")}>Continue <ArrowRight /></button>
							<div className="clearfix"></div>
						</div>
					</Tab>
					<Tab eventKey="parts" title="Parts" disabled={survey == null}>
						<div className={tabContentStyle}>
							<PartsForm inspection={inspection} survey={survey} job={job} readOnly={isReadOnly} saveAction={saveWhileEditing}/>
						</div>
					</Tab>
					<Tab eventKey="finalization" title={readyFinal? "Final": with_warning_label("Final")} disabled={survey == null}>
						<div className={tabContentStyle}>
							<fieldset disabled={isReadOnly}>
								<FinalizationForm inspection={inspection} survey={survey} job={job} onComplete={submitForm}
								onFinalFormChange={() => {setReadyFinal((!inspection.rentalMode || !!inspection.rentalDisposition) && !!inspection.result && !!inspection.disposition) }} saveAction={saveWhileEditing} />
							</fieldset>
						</div>
					</Tab>
				</Tabs>
			</div>
		)
	}
	let mainPageElement = mainPage();

	return(
		pageCondition != "ready" ? <LoadingComponent /> : mainPageElement
	);
}
